import { type IPrimaryCategoryRepository } from '@webapp/core/business-logic/interfaces/IPrimaryCategoryRepository'
import { mapLocation } from '@webapp/core/data-access/mappers/mapLocation'
import { mapLocationV2 } from '@webapp/core/data-access/mappers/mapLocationV2'
import { type GetPrimaryCategoryListLegacyResponse } from '@webapp/core/data-access/repositories/PrimaryCategoryRepository/types/GetPrimaryCategoryListLegacyResponse'
import { type DetailedPrimaryCategoryConstructor } from '@webapp/core/entities/internal/DetailedPrimaryCategory'
import type { RequestTypeEnum } from '@webapp/core/entities/internal/enums/RequestTypeEnum'
import { type PrimaryCategoryConstructor } from '@webapp/core/entities/internal/PrimaryCategory'
import { formatRepoFiltersToApiFilters } from '@webapp/core/utils/repositories/formatRepoFiltersToApiFilters'
import { throwRepoErrorFromApiV3Error } from '@webapp/core/utils/repositories/throwRepoErrorFromApiV3Error'
import { type RepoFilterParams } from '@webapp/infrastructure/interfaces/repositories/RepoFilterParams'
import { type RepoPaginatedResponse } from '@webapp/infrastructure/interfaces/repositories/RepoPaginatedResponse'
import { type RepoStandardResponse } from '@webapp/infrastructure/interfaces/repositories/RepoStandardResponse'
import { type IHttpClient } from '@webapp/infrastructure/interfaces/services/IHttpClient'
import {
  type GetPrimaryCategoryByIdResponse,
  type GetPrimaryCategoryListResponse,
} from './types'

interface Dependency {
  httpClient: IHttpClient
}

interface PrimaryCategoryListRepoFilterParams extends Omit<
  RepoFilterParams,
  'pageSize'
> {
  pageSize?: number | 'all' // use 'all' to get around 1000 item API limit (use with caution)
}

export class PrimaryCategoryRepository implements IPrimaryCategoryRepository {
  constructor(private readonly deps: Dependency) {}

  async list(
    params: {
      userId?: number
      requestType?: RequestTypeEnum
      scopeUser?: number
      legalEntityId?: number
    } & PrimaryCategoryListRepoFilterParams = {}
  ): Promise<RepoPaginatedResponse<PrimaryCategoryConstructor>> {
    const {
      userId,
      requestType,
      scopeUser,
      legalEntityId,
      pageSize,
      ...filterParams
    } = params

    try {
      const res =
        await this.deps.httpClient.get<GetPrimaryCategoryListResponse>(
          `/api/v3/locations/`,
          {
            params: {
              requestable_user: userId,
              requestable: requestType,
              scope_user: scopeUser,
              legal_entity: legalEntityId,
              ...formatRepoFiltersToApiFilters(filterParams),
              page_size: pageSize,
            },
          }
        )

      const primaryCategories = res.data.data
      const metadata = res.data.metadata

      return {
        data: primaryCategories.map((location) => mapLocation(location)),
        pagination: {
          count: metadata?.pagination?.count || 0,
          page: metadata?.pagination?.current_page || 0,
          pageSize: metadata?.pagination?.page_size || 0,
          numPages: metadata?.pagination?.num_pages || 0,
        },
      }
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }

  async getById(
    primaryCategoryId: number
  ): Promise<RepoStandardResponse<PrimaryCategoryConstructor>> {
    try {
      const res =
        await this.deps.httpClient.get<GetPrimaryCategoryByIdResponse>(
          `/api/v3/locations/${primaryCategoryId}/`
        )

      return { data: mapLocation(res.data.data) }
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }

  async legacyList(
    isActive: boolean = true,
    params: RepoFilterParams = {}
  ): Promise<RepoPaginatedResponse<DetailedPrimaryCategoryConstructor>> {
    const res =
      await this.deps.httpClient.get<GetPrimaryCategoryListLegacyResponse>(
        '/api/v2/locations/',
        {
          params: {
            active: isActive,
            ...formatRepoFiltersToApiFilters(params),
          },
        }
      )

    const primaryCategories = res.data.data
    const metadata = res.data.metadata

    return {
      data: primaryCategories.map((location) => mapLocationV2(location)),
      pagination: {
        count: metadata?.pagination?.count || 0,
        page: metadata?.pagination?.current_page || 0,
        pageSize: metadata?.pagination?.page_size || 0,
        numPages: metadata?.pagination?.num_pages || 0,
      },
    }
  }
}
